import {
  BACKUP_STALE_HOURS,
  type BackupKind,
  BackupRun,
  type BackupStatusResponse,
  StorageUsage,
  formatDateTime,
} from '@deizi/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { z } from 'zod';
import { ReadOnlyNote } from '../../../components/Form';
import { Icon } from '../../../components/Icon';
import { PageHead } from '../../../components/PageHead';
import { Alert, ErrorBox, Skeleton } from '../../../components/States';
import { useToast } from '../../../components/Toast';
import { api, newIdempotencyKey } from '../../../lib/api';
import {
  backupsKey,
  formatAge,
  formatBytes,
  useBackupRuns,
  useBackupStatus,
} from '../../../lib/backups-api';
import { errorMessage } from '../../../lib/messages';
import { useTrash } from '../../../lib/data-api';
import { useIsAdmin } from '../../../lib/session';
import { AuditTab } from './AuditTab';
import { ExportTab } from './ExportTab';
import { ImportTab } from './ImportTab';
import { TrashTab } from './TrashTab';

const TABS = [
  { key: 'backups', label: 'Backups' },
  { key: 'importar', label: 'Importar' },
  { key: 'exportar', label: 'Exportar' },
  { key: 'lixeira', label: 'Lixeira' },
  { key: 'auditoria', label: 'Auditoria' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

type Destination = BackupStatusResponse['destinations'][number];

/** What to tell people when a destination fails (keys live in the server's .env). */
function destinationHelp(d: Destination, isAdmin: boolean): string | null {
  if (d.lastStatus !== 'failed') return null;
  if (!isAdmin) return 'Avise o Lucas.';
  if (d.errorCode === 'auth') {
    return d.kind === 's3'
      ? `A chave da nuvem foi recusada: atualize BACKUP_REPO_${d.slot}_AWS_ACCESS_KEY_ID e _AWS_SECRET_ACCESS_KEY no .env do servidor e reinicie o serviço de backup.`
      : 'A senha dos backups (RESTIC_PASSWORD) não abre este repositório: confira o .env do servidor.';
  }
  if (d.errorCode === 'unreachable') {
    return d.kind === 'local'
      ? 'Não deu para acessar a pasta: o disco está conectado e montado?'
      : `Não deu para acessar o destino: confira a internet e BACKUP_REPO_${d.slot} no .env.`;
  }
  return null;
}

const KIND_LABEL: Record<Destination['kind'], string> = {
  local: 'Pasta local',
  s3: 'Nuvem (S3 compatível)',
  other: 'Outro',
};

function runLabel(r: BackupRun): string {
  return r.kind === 'backup' ? 'Backup' : 'Teste de restauração';
}

function duration(r: BackupRun): string {
  if (!r.startedAt || !r.finishedAt) return '—';
  const s = Math.max(1, Math.round((Date.parse(r.finishedAt) - Date.parse(r.startedAt)) / 1000));
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`;
}

const RUN_BADGE: Record<BackupRun['status'], [string, string]> = {
  requested: ['badge', 'Na fila'],
  running: ['badge info', 'Rodando'],
  succeeded: ['badge ok', 'Ok'],
  partial: ['badge warn', 'Parcial'],
  failed: ['badge err', 'Falhou'],
};

function useStartRun() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (kind: BackupKind) =>
      api.post(
        '/backups/runs',
        z.object({ run: BackupRun, workerOnline: z.boolean() }),
        { kind },
        { idempotencyKey: newIdempotencyKey() },
      ),
    onSuccess: (res, kind) => {
      void qc.invalidateQueries({ queryKey: backupsKey });
      toast(
        res.workerOnline
          ? kind === 'backup'
            ? 'Backup começou. Pode continuar usando o sistema.'
            : 'Teste de restauração começou.'
          : 'Ficou na fila: o serviço de backup está parado.',
      );
    },
    onError: (err) => toast(errorMessage(err)),
  });
}

function BackupsTab({ status, isAdmin }: { status: BackupStatusResponse; isAdmin: boolean }) {
  const runs = useBackupRuns(
    `${status.lastRun?.id ?? ''}:${status.running?.id ?? ''}:${status.running?.status ?? ''}`,
  );
  const start = useStartRun();
  const last = status.lastComplete;
  const bad = !last || last.stale || status.destinations.some((d) => d.lastStatus === 'failed');
  const running = status.running;
  const schedule = status.schedule;
  const test = status.lastRestoreTest;

  return (
    <>
      {!status.worker.online ? (
        <Alert tone="err">
          <b>Serviço de backup parado.</b>{' '}
          {status.worker.seenAt
            ? `Visto pela última vez em ${formatDateTime(status.worker.seenAt)}.`
            : 'Ele ainda não rodou neste servidor.'}{' '}
          {isAdmin
            ? 'No servidor: docker compose up -d backup (veja docs/DEPLOY.md, seção 6).'
            : 'Avise o Lucas.'}
        </Alert>
      ) : null}
      <section
        className="card col"
        style={{ gap: 16, borderColor: bad ? 'var(--err)' : undefined }}
        aria-label="Situação dos backups"
      >
        <div className="row between wrap" style={{ gap: 12 }}>
          <div className="row" style={{ gap: 12 }}>
            <span
              className="bkic"
              style={
                bad
                  ? { background: 'var(--err-bg)', color: 'var(--err)' }
                  : { background: 'var(--ok-bg)', color: 'var(--ok)' }
              }
            >
              <Icon name={bad ? 'alert' : 'check'} size={24} strokeWidth={2.4} />
            </span>
            <div>
              <h2 style={{ color: bad ? 'var(--err)' : 'var(--ok)' }}>
                {last
                  ? `Último backup completo há ${formatAge(last.ageHours)}`
                  : 'Nenhum backup completo ainda'}
              </h2>
              <p className="sm muted">
                {last
                  ? `${formatDateTime(last.at)} · ${status.destinations.map((d) => d.name).join(' e ') || 'sem destinos'}${last.stale ? ` · o limite é ${BACKUP_STALE_HOURS} horas` : ''}`
                  : 'Faça o primeiro backup antes de cadastrar dados reais.'}
              </p>
            </div>
          </div>
          {isAdmin ? (
            <button
              type="button"
              className="btn p"
              onClick={() => start.mutate('backup')}
              disabled={!!running || start.isPending}
              aria-busy={!!running}
            >
              <Icon name="box" size={18} />
              {running?.kind === 'backup' ? 'Fazendo backup…' : 'Fazer backup agora'}
            </button>
          ) : (
            <span className="badge">
              <Icon name="lock" size={14} />
              Só Admin altera
            </span>
          )}
        </div>

        {running ? (
          <div
            className="card sunk col"
            style={{ gap: 10, padding: 16 }}
            role="status"
            aria-live="polite"
          >
            <div className="row between">
              <span style={{ fontWeight: 500 }}>
                {running.status === 'requested'
                  ? `${running.kind === 'backup' ? 'Backup' : 'Teste de restauração'} na fila`
                  : (running.phase ?? 'Começando')}
              </span>
              <span className="tab sm muted">{running.percent}%</span>
            </div>
            <div className="layers" style={{ marginTop: 8 }}>
              <div className="fill" style={{ width: `${running.percent}%` }} />
              <div className="nozzle" style={{ left: `${running.percent}%` }} />
            </div>
            <p className="xs muted">
              Pode continuar usando o sistema. Pedidos novos entram no próximo backup.
            </p>
          </div>
        ) : null}

        <div className="card flat" style={{ borderRadius: 12 }}>
          <div className="gt">
            <div className="gtr head" style={{ gridTemplateColumns: '1.4fr 1fr 2fr 1fr' }}>
              <span>Destino</span>
              <span>Último sucesso</span>
              <span>Situação</span>
              <span>Tipo</span>
            </div>
            {status.destinations.length === 0 ? (
              <p className="sm muted" style={{ padding: '12px 16px' }}>
                Nenhum destino configurado. Os destinos ficam no .env do servidor (BACKUP_REPO_1…3).
              </p>
            ) : null}
            {status.destinations.map((d) => {
              const help = destinationHelp(d, isAdmin);
              return (
                <div
                  key={d.slot}
                  className={d.lastStatus === 'failed' ? 'gtr hl' : 'gtr'}
                  style={{ gridTemplateColumns: '1.4fr 1fr 2fr 1fr' }}
                >
                  <span style={{ fontWeight: 500 }}>{d.name}</span>
                  <span className="tab sm">
                    {d.lastSuccessAt ? formatDateTime(d.lastSuccessAt) : '—'}
                  </span>
                  <span>
                    {d.lastStatus === 'ok' ? (
                      <span className="badge ok">
                        <Icon name="check" size={14} />
                        Ok
                      </span>
                    ) : d.lastStatus === 'never' ? (
                      <span className="badge">Ainda não rodou</span>
                    ) : (
                      <>
                        <span className="badge err">
                          <Icon name="alert" size={14} />
                          Falhou {d.consecutiveFailures}×
                        </span>
                        <span
                          className="xs"
                          style={{ color: 'var(--err)', marginTop: 4, display: 'block' }}
                        >
                          {help ?? d.error}
                        </span>
                        {help && d.error ? (
                          <span className="xs muted" style={{ display: 'block' }}>
                            {d.error}
                          </span>
                        ) : null}
                      </>
                    )}
                  </span>
                  <span className="sm muted">{KIND_LABEL[d.kind]}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="row wrap sm" style={{ gap: 24, alignItems: 'center' }}>
          <span>
            <span className="muted">Banco de dados</span>{' '}
            {status.sizes ? formatBytes(status.sizes.databaseBytes) : '—'}
          </span>
          <span>
            <span className="muted">Arquivos</span>{' '}
            {status.sizes
              ? `${formatBytes(status.sizes.filesBytes)} · ${status.sizes.filesCount} ${status.sizes.filesCount === 1 ? 'arquivo' : 'arquivos'}`
              : '—'}
          </span>
          <span>
            <span className="muted">Último teste de restauração</span>{' '}
            {test ? (
              <span style={{ color: test.ok ? undefined : 'var(--err)' }}>
                {formatDateTime(test.at)}
                {test.ok ? ' · ok' : ' · falhou'}
              </span>
            ) : (
              'nunca'
            )}
            {status.restoreTestDue ? (
              <span className="badge warn" style={{ marginLeft: 8 }}>
                Teste pendente
              </span>
            ) : null}
          </span>
          <span className="grow" />
          {isAdmin ? (
            <button
              type="button"
              className="btn sm"
              onClick={() => start.mutate('restore_test')}
              disabled={!!running || start.isPending || !last}
              title={last ? undefined : 'Faça um backup primeiro'}
            >
              {running?.kind === 'restore_test' ? 'Testando…' : 'Testar restauração'}
            </button>
          ) : (
            <span className="row sm muted" style={{ gap: 6 }}>
              <Icon name="lock" size={14} />
              Só Admin testa a restauração
            </span>
          )}
        </div>
        {test && !test.ok && test.error ? (
          <p className="help err">
            <Icon name="alert" size={14} />
            {test.error}
          </p>
        ) : null}
      </section>

      <section className="card col" style={{ gap: 8 }}>
        <h2>Rotina</h2>
        <div className="kv">
          <span className="muted">Backup automático</span>
          <span className="v">
            {schedule ? `todo dia às ${schedule.time.replace(':', 'h')}` : '—'}
          </span>
        </div>
        <div className="kv">
          <span className="muted">Alerta quando passar de</span>
          <span className="v">{BACKUP_STALE_HOURS} horas sem backup completo</span>
        </div>
        <div className="kv">
          <span className="muted">Teste de restauração</span>
          <span className="v">
            {schedule
              ? `automático a cada ${schedule.restoreTestDays} dias, depois do backup da noite`
              : '—'}
          </span>
        </div>
        <div className="kv">
          <span className="muted">Cópias guardadas</span>
          <span className="v">
            {schedule
              ? `${schedule.keepDaily} diárias, ${schedule.keepWeekly} semanais, ${schedule.keepMonthly} mensais`
              : '—'}
          </span>
        </div>
      </section>

      <section className="card flat">
        <div className="row between" style={{ padding: '16px 18px 6px' }}>
          <h2>Últimas execuções</h2>
        </div>
        <div className="gt">
          {(runs.data?.data ?? []).map((r) => {
            const [cls, label] = RUN_BADGE[r.status];
            return (
              <div
                key={r.id}
                className="gtr"
                style={{ gridTemplateColumns: '150px 1.2fr 90px 80px 2fr' }}
              >
                <span className="tab sm">{formatDateTime(r.finishedAt ?? r.requestedAt)}</span>
                <span className="sm">
                  {runLabel(r)}{' '}
                  <span className="xs muted">
                    ·{' '}
                    {r.trigger === 'schedule'
                      ? 'automático'
                      : `pedido por ${r.requestedBy ?? 'linha de comando'}`}
                  </span>
                </span>
                <span>
                  <span className={cls}>{label}</span>
                </span>
                <span className="sm tab muted">{duration(r)}</span>
                <span className="xs muted trunc" title={r.error ?? undefined}>
                  {r.error ??
                    (r.details.restore
                      ? `${r.details.restore.tables} tabelas, ${r.details.restore.rows} linhas e ${r.details.restore.files} arquivos conferidos`
                      : r.details.destinations
                          .map((d) => `${d.name}: ${d.ok ? 'ok' : 'falhou'}`)
                          .join(' · '))}
                </span>
              </div>
            );
          })}
          {runs.data && runs.data.data.length === 0 ? (
            <p className="sm muted" style={{ padding: '12px 18px 16px' }}>
              Nenhuma execução ainda.
            </p>
          ) : null}
        </div>
      </section>
    </>
  );
}

/** Configurações · Dados e backups (board Dados, Backups tab; the other tabs come later). */
const GROUP_LABELS = {
  media: 'Fotos e vídeos dos produtos',
  product_files: 'Arquivos dos produtos',
  quotes: 'Arquivos de orçamentos',
  other: 'Outros',
} as const;

/** Disk used by uploads (included in every backup). */
function StorageCard() {
  const usage = useQuery({
    queryKey: ['storage-usage'],
    queryFn: () => api.get('/storage/usage', StorageUsage),
  });
  if (!usage.data) return null;
  const u = usage.data;
  return (
    <section className="card col" style={{ gap: 8 }}>
      <div className="row between">
        <h2>Armazenamento</h2>
        <span className="fr tab" style={{ fontSize: 20 }}>
          {formatBytes(u.totalBytes)}
        </span>
      </div>
      {u.groups
        .filter((g) => g.files > 0)
        .map((g) => (
          <div key={g.group} className="kv">
            <span className="muted">{GROUP_LABELS[g.group]}</span>
            <span className="v">
              {formatBytes(g.bytes)} · {g.files} {g.files === 1 ? 'arquivo' : 'arquivos'}
            </span>
          </div>
        ))}
      <div className="kv">
        <span className="muted">Vídeos (originais e prévias)</span>
        <span className="v">{formatBytes(u.videoBytes)}</span>
      </div>
      {u.products.length > 0 ? (
        <>
          <p className="lbl" style={{ marginTop: 6 }}>
            Produtos que mais ocupam
          </p>
          {u.products.slice(0, 5).map((p) => (
            <div key={p.id} className="kv sm">
              <Link to={`/produtos/${p.id}/midia`}>{p.name}</Link>
              <span className="v">{formatBytes(p.bytes)}</span>
            </div>
          ))}
        </>
      ) : null}
      <p className="xs muted">
        Tudo isso entra no backup. Vídeos longos ocupam mais: na aba Mídia do produto, “Guardar só a
        prévia” apaga o original.
      </p>
    </section>
  );
}

export function DataPage() {
  const isAdmin = useIsAdmin();
  const status = useBackupStatus();
  const params = useParams();
  const tab: TabKey = TABS.find((t) => t.key === params.tab)?.key ?? 'backups';
  const trash = useTrash();
  const trashCount = trash.data?.data.length ?? 0;
  return (
    <>
      <PageHead
        crumb="Configurações"
        title="Dados e backups"
        actions={
          isAdmin ? undefined : (
            <div className="row" style={{ gap: 8 }}>
              <span className="badge info">Vendo como Operadora</span>
              <span className="badge">
                <Icon name="lock" size={14} />
                Só Admin altera
              </span>
            </div>
          )
        }
      />
      {isAdmin ? null : (
        <ReadOnlyNote>
          <b>Só Admin altera.</b> Você entrou como Operadora: pode ver a situação dos backups, a
          lixeira e a auditoria. Fazer backup, importar, exportar e restaurar é com o Lucas.
        </ReadOnlyNote>
      )}
      <nav className="tabs" aria-label="Seções">
        {TABS.map((t) => (
          <Link
            key={t.key}
            to={t.key === 'backups' ? '/configuracoes/dados' : `/configuracoes/dados/${t.key}`}
            className={tab === t.key ? 'on' : ''}
            aria-current={tab === t.key ? 'page' : undefined}
          >
            {t.key === 'lixeira' && trashCount ? `Lixeira · ${trashCount}` : t.label}
          </Link>
        ))}
      </nav>
      {tab === 'importar' ? <ImportTab /> : null}
      {tab === 'exportar' ? <ExportTab /> : null}
      {tab === 'lixeira' ? <TrashTab /> : null}
      {tab === 'auditoria' ? <AuditTab /> : null}
      {tab !== 'backups' ? null : status.isPending ? <Skeleton rows={2} height={160} /> : null}
      {tab === 'backups' && status.isError ? (
        <ErrorBox
          title="Não foi possível carregar os backups"
          onRetry={() => void status.refetch()}
        />
      ) : null}
      {tab === 'backups' && status.data ? (
        <BackupsTab status={status.data} isAdmin={isAdmin} />
      ) : null}
      {tab === 'backups' && status.data ? <StorageCard /> : null}
    </>
  );
}
